/**
 * Execution Recipe Service
 *
 * Read/write access to execution_recipe_templates (Canon Doc 21).
 * Built-in rows are global; custom rows are workspace-scoped (migration 517).
 */

import { getSupabaseClient } from './supabaseClient';
import type {
  ApproachKind,
  ExecutionRecipeTemplate,
  RecipeGovernancePolicyRef,
  RecipeProcessArtifact,
  ResourceAllocation,
} from '../types/work-type-recipes';

const RECIPE_TABLE = 'execution_recipe_templates';

const RECIPE_SELECT =
  'id, work_type_ref, name, approach_kind, uncertainty_multiplier, ' +
  'required_process_artifacts, governance_policy_refs, resource_allocations, description, is_builtin, ' +
  'workspace_id, active, metadata, version_number, supersedes_id, created_at, updated_at';

function rowToRecipe(row: Record<string, unknown>): ExecutionRecipeTemplate {
  return {
    id: row.id as string,
    work_type_ref: row.work_type_ref as string,
    name: row.name as string,
    approach_kind: row.approach_kind as ExecutionRecipeTemplate['approach_kind'],
    uncertainty_multiplier: Number(row.uncertainty_multiplier),
    required_process_artifacts:
      (row.required_process_artifacts as ExecutionRecipeTemplate['required_process_artifacts']) ?? [],
    governance_policy_refs:
      (row.governance_policy_refs as ExecutionRecipeTemplate['governance_policy_refs']) ?? [],
    resource_allocations:
      (row.resource_allocations as ExecutionRecipeTemplate['resource_allocations']) ?? [],
    description: (row.description as string) ?? null,
    is_builtin: row.is_builtin as boolean,
    workspace_id: (row.workspace_id as string) ?? null,
    active: row.active !== false,
    metadata: (row.metadata as Record<string, unknown>) ?? {},
    version_number: row.version_number != null ? Number(row.version_number) : 1,
    supersedes_id: (row.supersedes_id as string) ?? null,
    created_at: row.created_at as string,
    updated_at: (row.updated_at as string) ?? undefined,
  };
}

/**
 * Drops superseded versions (inactive rows that some other row supersedes) from a
 * result set, while keeping plainly archived rows (inactive, no successor) so they
 * remain re-activatable in settings. Version history uses {@link listRecipeVersions}.
 */
function dropSupersededVersions(rows: ExecutionRecipeTemplate[]): ExecutionRecipeTemplate[] {
  const supersededIds = new Set(rows.map((r) => r.supersedes_id).filter(Boolean) as string[]);
  return rows.filter((r) => r.active || !supersededIds.has(r.id));
}

/** Fetch built-in recipes plus workspace customs. */
export async function listExecutionRecipes(workspaceId?: string | null): Promise<{
  data: ExecutionRecipeTemplate[];
  error: string | null;
}> {
  const supabase = getSupabaseClient();
  if (!supabase) return { data: [], error: 'Supabase client unavailable' };

  let query = supabase.from(RECIPE_TABLE).select(RECIPE_SELECT);

  if (workspaceId) {
    query = query.or(`is_builtin.eq.true,workspace_id.eq.${workspaceId}`);
  } else {
    query = query.eq('is_builtin', true);
  }

  const { data, error } = await query.order('is_builtin', { ascending: false }).order('name');

  if (error) return { data: [], error: error.message };
  return {
    data: dropSupersededVersions((data as unknown as Record<string, unknown>[]).map(rowToRecipe)),
    error: null,
  };
}

export interface CustomWorkTypeInput {
  work_type_ref: string;
  name: string;
  approach_kind: ApproachKind;
  uncertainty_multiplier: number;
  description?: string | null;
  required_process_artifacts?: RecipeProcessArtifact[];
  governance_policy_refs?: RecipeGovernancePolicyRef[];
  resource_allocations?: ResourceAllocation[];
  metadata?: Record<string, unknown>;
  active?: boolean;
}

export async function createCustomWorkType(
  workspaceId: string,
  input: CustomWorkTypeInput,
): Promise<{ data: ExecutionRecipeTemplate | null; error: string | null }> {
  const supabase = getSupabaseClient();
  if (!supabase) return { data: null, error: 'Supabase client unavailable' };

  const { data, error } = await supabase
    .from(RECIPE_TABLE)
    .insert({
      workspace_id: workspaceId,
      work_type_ref: input.work_type_ref,
      name: input.name,
      approach_kind: input.approach_kind,
      uncertainty_multiplier: input.uncertainty_multiplier,
      description: input.description ?? null,
      required_process_artifacts: input.required_process_artifacts ?? [],
      governance_policy_refs: input.governance_policy_refs ?? [],
      resource_allocations: input.resource_allocations ?? [],
      metadata: input.metadata ?? {},
      active: input.active ?? true,
      is_builtin: false,
    })
    .select(RECIPE_SELECT)
    .single();

  if (error) return { data: null, error: error.message };
  return { data: rowToRecipe(data as Record<string, unknown>), error: null };
}

/**
 * Mint a new active version of a recipe via the transactional versioning RPC
 * (Build Contract §2.3a). Deactivates the prior active version; in-flight plans
 * stay pinned to the frozen row they resolved against. Revising a built-in mints
 * a workspace-scoped shadow version (pass workspaceId).
 */
export async function reviseWorkTypeRecipe(
  recipeId: string,
  changes: Record<string, unknown>,
  reason: string,
  opts?: { source?: 'manual' | 'calibration'; workspaceId?: string | null },
): Promise<{ data: ExecutionRecipeTemplate | null; error: string | null }> {
  const supabase = getSupabaseClient();
  if (!supabase) return { data: null, error: 'Supabase client unavailable' };

  const { data: newId, error } = await supabase.rpc('revise_work_type_recipe', {
    p_recipe_id: recipeId,
    p_changes: changes,
    p_reason: reason,
    p_source: opts?.source ?? 'manual',
    p_workspace_id: opts?.workspaceId ?? null,
  });
  if (error) return { data: null, error: error.message };

  const { data: row, error: fetchErr } = await supabase
    .from(RECIPE_TABLE)
    .select(RECIPE_SELECT)
    .eq('id', newId as string)
    .single();
  if (fetchErr) return { data: null, error: fetchErr.message };
  return { data: rowToRecipe(row as unknown as Record<string, unknown>), error: null };
}

/** Archive / un-archive a custom recipe. Not a revision — a plain visibility toggle. */
export async function setWorkTypeActive(
  id: string,
  active: boolean,
): Promise<{ data: ExecutionRecipeTemplate | null; error: string | null }> {
  const supabase = getSupabaseClient();
  if (!supabase) return { data: null, error: 'Supabase client unavailable' };

  const { data, error } = await supabase
    .from(RECIPE_TABLE)
    .update({ active })
    .eq('id', id)
    .eq('is_builtin', false)
    .select(RECIPE_SELECT)
    .single();

  if (error) return { data: null, error: error.message };
  return { data: rowToRecipe(data as unknown as Record<string, unknown>), error: null };
}

/**
 * Update a custom work type. Content changes are versioning revisions and go
 * through {@link reviseWorkTypeRecipe}; an `active`-only change is a plain archive
 * toggle. This preserves the "new version → new row" invariant (Canon 111 §3.1).
 *
 * `workspaceId` must accompany a built-in target: the revision RPC refuses to
 * revise a built-in without it, since that revision mints the workspace's shadow
 * version rather than editing the global row. For custom targets it is inert
 * (the RPC coalesces to the row's own workspace).
 */
export async function updateCustomWorkType(
  id: string,
  patch: Partial<Omit<CustomWorkTypeInput, 'work_type_ref'>>,
  reason?: string,
  workspaceId?: string | null,
): Promise<{ data: ExecutionRecipeTemplate | null; error: string | null }> {
  const supabase = getSupabaseClient();
  if (!supabase) return { data: null, error: 'Supabase client unavailable' };

  const { active, ...content } = patch;
  const changes: Record<string, unknown> = {};
  if (content.name !== undefined) changes.name = content.name;
  if (content.approach_kind !== undefined) changes.approach_kind = content.approach_kind;
  if (content.uncertainty_multiplier !== undefined)
    changes.uncertainty_multiplier = content.uncertainty_multiplier;
  if (content.description !== undefined) changes.description = content.description;
  if (content.required_process_artifacts !== undefined)
    changes.required_process_artifacts = content.required_process_artifacts;
  if (content.governance_policy_refs !== undefined)
    changes.governance_policy_refs = content.governance_policy_refs;
  if (content.resource_allocations !== undefined)
    changes.resource_allocations = content.resource_allocations;
  if (content.metadata !== undefined) changes.metadata = content.metadata;

  if (Object.keys(changes).length > 0) {
    const revised = await reviseWorkTypeRecipe(
      id,
      changes,
      reason?.trim() || 'Edited in Work Types settings',
      { workspaceId },
    );
    if (revised.error || !revised.data) return revised;
    if (active !== undefined && active !== revised.data.active) {
      return setWorkTypeActive(revised.data.id, active);
    }
    return revised;
  }

  if (active !== undefined) return setWorkTypeActive(id, active);

  const { data, error } = await supabase
    .from(RECIPE_TABLE)
    .select(RECIPE_SELECT)
    .eq('id', id)
    .single();
  if (error) return { data: null, error: error.message };
  return { data: rowToRecipe(data as unknown as Record<string, unknown>), error: null };
}

/** All versions for a work_type_ref (active + superseded), newest first. */
export async function listRecipeVersions(
  workType: string,
  workspaceId: string | null | undefined,
): Promise<{ data: ExecutionRecipeTemplate[]; error: string | null }> {
  const supabase = getSupabaseClient();
  if (!supabase) return { data: [], error: 'Supabase client unavailable' };

  let query = supabase.from(RECIPE_TABLE).select(RECIPE_SELECT).eq('work_type_ref', workType);
  if (workspaceId) {
    query = query.or(`is_builtin.eq.true,workspace_id.eq.${workspaceId}`);
  } else {
    query = query.eq('is_builtin', true);
  }

  const { data, error } = await query
    .order('workspace_id', { ascending: false, nullsFirst: false })
    .order('version_number', { ascending: false });
  if (error) return { data: [], error: error.message };
  return { data: (data as unknown as Record<string, unknown>[]).map(rowToRecipe), error: null };
}

export async function deleteCustomWorkType(id: string): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  if (!supabase) return { error: 'Supabase client unavailable' };

  const { error } = await supabase.from(RECIPE_TABLE).delete().eq('id', id).eq('is_builtin', false);
  return { error: error?.message ?? null };
}

/** Resolve recipe id for an item's work_type within a workspace. */
export async function resolveRecipeIdForWorkType(
  workType: string,
  workspaceId: string | null | undefined,
): Promise<string | null> {
  const supabase = getSupabaseClient();
  if (!supabase) return null;

  let query = supabase
    .from(RECIPE_TABLE)
    .select('id')
    .eq('work_type_ref', workType)
    .eq('active', true);

  if (workspaceId) {
    query = query.or(`is_builtin.eq.true,workspace_id.eq.${workspaceId}`);
  } else {
    query = query.eq('is_builtin', true);
  }

  // Workspace (shadow/custom) active row wins over the global built-in for the ref.
  const { data } = await query.order('workspace_id', { ascending: false, nullsFirst: false }).limit(1);
  return data?.[0]?.id ?? null;
}
